import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintWithGlyph, HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { CopyToClipboardGlyph } from "@/components/ui/CopyToClipboardGlyph";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import type { MrpAmlChangeRequestHistoryRow, MrpAmlEntry, MrpAmlSourcingSupplierLink } from "@/lib/mrp";

import { cn } from "@/lib/utils";

import {
  AML_ACTIVE_COLUMN_HINT,
  AML_END_DATE_COLUMN_HINT,
  AML_STATUS_COLUMN_HINT,
  formatAmlEndDate,
  formatAmlMpnAliasesLine,
  amlMpnAliasesDraftFromRow,
  parseAmlMpnAliasesDraft,
  formatSupportingDocsLine,
  sortAmlAlternateRows,
  summarizeAmlChangeRequestAction,
} from "./aml/amlEntryUtils";

function alternateTabTitle(row: MrpAmlEntry): string {
  const manufacturer = row.manufacturer_name?.trim() || "—";
  const manufacturerPartNumber = row.mpn?.trim() || "No MPN";
  return `${manufacturer} · ${manufacturerPartNumber}`;
}

function AmlAlternateTab({
  row,
  selected,
  onSelect,
}: {
  row: MrpAmlEntry;
  selected: boolean;
  onSelect: () => void;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelTruncated, setLabelTruncated] = useState(false);
  const manufacturerPartNumber = row.mpn?.trim() ?? "";
  const tabTitle = alternateTabTitle(row);

  useLayoutEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) return;

    const updateTruncation = () => {
      setLabelTruncated(labelElement.scrollWidth > labelElement.clientWidth);
    };

    updateTruncation();
    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(labelElement);
    return () => resizeObserver.disconnect();
  }, [tabTitle]);

  const labelText = (
    <>
      {row.manufacturer_name?.trim() || "—"} ·{" "}
      <span className="font-mono font-medium">
        {manufacturerPartNumber.length > 0 ? manufacturerPartNumber : "No MPN"}
      </span>
    </>
  );

  const labelNode = (
    <span ref={labelRef} className="min-w-0 truncate">
      {labelText}
    </span>
  );

  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={selected}
      id={`aml-alternate-tab-${row.id}`}
      aria-controls={`aml-alternate-panel-${row.id}`}
      className={cn(
        "w-max max-w-full cursor-pointer rounded-t-md border px-3 py-2 text-left text-sm",
        selected
          ? "relative z-10 border-b-0 border-slate-200 bg-white font-medium text-slate-900 shadow-sm"
          : "border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
      )}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="flex max-w-full items-center gap-1 overflow-hidden">
        {labelTruncated ? (
          <HoverHintContent hoverHint={tabTitle} cursor="default" className="min-w-0 shrink">
            {labelNode}
          </HoverHintContent>
        ) : (
          labelNode
        )}
        {manufacturerPartNumber.length > 0 ? (
          <CopyToClipboardGlyph text={manufacturerPartNumber} label="MPN" />
        ) : null}
      </div>
      {!row.active ? (
        <span className="block text-xs font-normal italic text-amber-900">inactive (listing only)</span>
      ) : null}
      {row.status === "deprecated" ? (
        <span className="block text-xs font-normal italic text-amber-900">deprecated</span>
      ) : null}
    </div>
  );
}

function DetailItem({
  label,
  hoverHint,
  children,
}: {
  label: string;
  hoverHint?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 sm:col-span-1">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {hoverHint ? (
          <HoverHintContent hoverHint={hoverHint}>
            <span className="cursor-help border-b border-dotted border-slate-400">{label}</span>
          </HoverHintContent>
        ) : (
          label
        )}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function amlSourcingChannelLabel(channel: string): string {
  if (channel === "catalog_web") return "Catalog webstore";
  if (channel === "contract_manufacturer") return "Contract manufacturer";
  return channel;
}

function AmlSourcingVendorsSection({ cpn, amlId }: { cpn: string; amlId: number }) {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const [supplierSearch, setSupplierSearch] = useState("");
  const [pickSupplierId, setPickSupplierId] = useState("");

  const sourcingQuery = useQuery({
    queryKey: ["mrp", "aml", amlId, "sourcing-suppliers"],
    queryFn: () =>
      apiFetch<{ links: MrpAmlSourcingSupplierLink[]; show_octopart: boolean }>(
        `/api/mrp/aml/${amlId}/sourcing-suppliers`,
      ),
    enabled: amlId > 0,
  });

  const saveMutation = useMutation({
    mutationFn: (body: { links: { supplier_data_id: number }[]; show_octopart?: boolean }) =>
      apiFetch(`/api/mrp/aml/${amlId}/sourcing-suppliers`, { method: "PUT", body }),
    onSuccess: async () => {
      await sourcingQuery.refetch();
      setPickSupplierId("");
      setSupplierSearch("");
      void queryClient.invalidateQueries({ queryKey: ["mrp", "shopping", "cpn", cpn] });
    },
  });

  const links = sourcingQuery.data?.links ?? [];

  const replaceLinks = (next: { supplier_data_id: number }[]): void => {
    saveMutation.mutate({ links: next });
  };

  const addVendor = (): void => {
    const id = Number.parseInt(pickSupplierId, 10);
    if (!Number.isFinite(id)) return;
    if (links.some((linkRow) => linkRow.id === id)) return;
    replaceLinks([
      ...links.map((linkRow) => ({ supplier_data_id: linkRow.id })),
      { supplier_data_id: id },
    ]);
  };

  const removeVendor = (supplierId: number): void => {
    replaceLinks(links.filter((linkRow) => linkRow.id !== supplierId).map((linkRow) => ({ supplier_data_id: linkRow.id })));
  };

  return (
    <Card className="mt-4 border-slate-200">
      <CardHeader className="py-3">
        <CardTitle className="text-base">Sourcing vendors (Shopping)</CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          Megaventory suppliers tracked for this alternate only. The Shopping tab shows one matrix column per vendor you
          add here (prices and product URLs are saved per alternate × vendor). Optionally enable an Octopart search column
          for this MPN row only.
        </p>
      </CardHeader>
      <CardBody className="space-y-3 pt-0">
        {sourcingQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading sourcing vendors…</p>
        ) : sourcingQuery.isError ? (
          <p className="text-sm text-red-600">Could not load sourcing vendors for this alternate.</p>
        ) : (
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm text-slate-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
                checked={sourcingQuery.data?.show_octopart ?? false}
                disabled={saveMutation.isPending || !hubWritesAllowed}
                onChange={(event) => {
                  if (!hubWritesAllowed) return;
                  saveMutation.mutate({
                    links: links.map((linkRow) => ({ supplier_data_id: linkRow.id })),
                    show_octopart: event.target.checked,
                  });
                }}
              />
              <span>
                Show <span className="font-medium">Octopart</span> on Shopping for this alternate (MPN search; not a
                Megaventory vendor).
              </span>
            </label>
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
              {links.length === 0 ? (
                <li className="px-3 py-2 text-sm text-slate-600">No vendors yet.</li>
              ) : (
                links.map((linkRow) => (
                  <li
                    key={linkRow.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <span>
                      {linkRow.mv_supplier_name ?? `Supplier #${linkRow.id}`} ·{" "}
                      <span className="text-slate-600">{amlSourcingChannelLabel(linkRow.channel)}</span>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeVendor(linkRow.id)}
                      disabled={saveMutation.isPending || !hubWritesAllowed}
                    >
                      Remove
                    </Button>
                  </li>
                ))
              )}
            </ul>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <Label>Add vendor</Label>
                <SupplierSearchableSelect
                  value={pickSupplierId}
                  disabled={!hubWritesAllowed}
                  minQueryLength={2}
                  onValueChange={(option) => setPickSupplierId(option?.value ?? "")}
                  searchValue={supplierSearch}
                  onSearchChange={setSupplierSearch}
                  placeholder="Type at least 2 characters to search…"
                  searchPlaceholder="Name or id (min. 2 characters)…"
                  emptyMessage="No suppliers match."
                />
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!pickSupplierId || saveMutation.isPending || !hubWritesAllowed}
                onClick={() => addVendor()}
              >
                Add
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Catalog webstore versus contract manufacturer is set on the supplier record (MRP → Suppliers), not per
              alternate.
            </p>
            {saveMutation.isError ? (
              <p className="text-sm text-red-600">
                {saveMutation.error instanceof Error ? saveMutation.error.message : "Could not save vendors."}
              </p>
            ) : null}
          </ReadOnlyHoverLayer>
        )}
      </CardBody>
    </Card>
  );
}

type AmlCpnDetailViewProps = {
  cpn: string;
};

/**
 * CPN / AML line detail: alternates, fields, and change-request history.
 * Rendered on the SKU detail "AML detail" tab (and linked from the AML list and elsewhere).
 */
export function AmlCpnDetailView({ cpn }: AmlCpnDetailViewProps) {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const [selectedAlternateId, setSelectedAlternateId] = useState<number | null>(null);
  const [notesRequestRow, setNotesRequestRow] = useState<MrpAmlEntry | null>(null);
  const [notesRequestDraft, setNotesRequestDraft] = useState("");
  const [aliasesRequestRow, setAliasesRequestRow] = useState<MrpAmlEntry | null>(null);
  const [aliasesRequestDraft, setAliasesRequestDraft] = useState("");

  const amlQuery = useQuery({
    queryKey: ["mrp", "aml", "cpn", cpn],
    queryFn: () =>
      apiFetch<{ rows: MrpAmlEntry[] }>("/api/mrp/aml", {
        query: { cpn, include_inactive: "true" },
      }),
    enabled: cpn.length > 0,
  });

  const historyQuery = useQuery({
    queryKey: ["mrp", "aml-requests", "history", cpn],
    queryFn: () =>
      apiFetch<{ rows: MrpAmlChangeRequestHistoryRow[] }>("/api/mrp/aml-requests/history", {
        query: { cpn },
      }),
    enabled: cpn.length > 0,
  });

  const rows = amlQuery.data?.rows ?? [];
  const sortedAlternates = useMemo(() => sortAmlAlternateRows(rows), [rows]);

  useEffect(() => {
    if (sortedAlternates.length === 0) {
      setSelectedAlternateId(null);
      return;
    }
    setSelectedAlternateId((previous) =>
      previous != null && sortedAlternates.some((row) => row.id === previous)
        ? previous
        : sortedAlternates[0].id,
    );
  }, [sortedAlternates]);

  const selectedRow =
    selectedAlternateId != null
      ? sortedAlternates.find((row) => row.id === selectedAlternateId)
      : undefined;

  const historyForAlternate = useMemo(() => {
    if (selectedAlternateId == null) return [];
    return (historyQuery.data?.rows ?? []).filter(
      (requestRow) => requestRow.approved_component_id === selectedAlternateId,
    );
  }, [historyQuery.data?.rows, selectedAlternateId]);

  type AmlRequestBody = { kind: "activate" } | { kind: "deactivate" } | { kind: "delete" };

  const notesMutation = useMutation({
    mutationFn: (input: { id: number; notes: string | null }) =>
      apiFetch(`/api/mrp/aml/${input.id}/notes`, {
        method: "PATCH",
        body: { notes: input.notes },
      }),
    onSuccess: async () => {
      setNotesRequestRow(null);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
  });

  const aliasesMutation = useMutation({
    mutationFn: (input: { id: number; mpn_aliases: string[] }) =>
      apiFetch(`/api/mrp/aml/${input.id}/mpn-aliases`, {
        method: "PATCH",
        body: { mpn_aliases: input.mpn_aliases },
      }),
    onSuccess: async () => {
      setAliasesRequestRow(null);
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
  });

  const amlRequestMutation = useMutation({
    mutationFn: (input: { id: number; body: AmlRequestBody }) =>
      apiFetch(`/api/mrp/aml/${input.id}/request`, { method: "POST", body: input.body }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["mrp", "aml-requests", "pending"] });
      await queryClient.refetchQueries({ queryKey: ["mrp", "aml-requests", "history", cpn] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
  });

  const requestBusy = amlRequestMutation.isPending || notesMutation.isPending || aliasesMutation.isPending;

  useEffect(() => {
    if (!aliasesRequestRow) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setAliasesRequestRow(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [aliasesRequestRow]);

  useEffect(() => {
    if (!notesRequestRow) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setNotesRequestRow(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [notesRequestRow]);

  const emptyMessage = (() => {
    if (amlQuery.isLoading) return null;
    if (cpn.length === 0) return "No CPN in the URL.";
    if (rows.length === 0) {
      return "No AML lines for this CPN. It may have been removed, or the CPN does not match any row in public.approved_components.";
    }
    return null;
  })();

  return (
    <div className="space-y-4">
      {cpn ? (
        <div className="space-y-1">
          <p className="text-sm text-slate-600">
            Each tab below is one approved alternate (manufacturer and MPN). Review source, workflow metadata, and the
            full change-request history for that line.
          </p>
          <p className="text-sm text-slate-600">
            AML matches on CPN, same as the SKU code. For the full list page, see{" "}
            <Link className="text-sky-700 underline hover:text-sky-900" to="/mrp/aml">
              Approved Material List
            </Link>
            .
          </p>
        </div>
      ) : null}

      {notesRequestRow ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => {
            notesMutation.reset();
            setNotesRequestRow(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="aml-cpn-notes-request-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="aml-cpn-notes-request-title" className="text-lg font-semibold text-slate-900">
                Edit notes
              </h2>
              <HoverHintWithGlyph
                hoverHint={`Notes for ${notesRequestRow.cpn} (row ${String(notesRequestRow.id)}). Manufacturer, MPN, and source cannot be changed here.`}
              />
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="aml-cpn-notes-request-input">Notes</Label>
                <Input
                  id="aml-cpn-notes-request-input"
                  value={notesRequestDraft}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setNotesRequestDraft(event.target.value)}
                  placeholder="Leave empty to clear notes"
                />
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <SaveChangesButton
                size="sm"
                variant="primary"
                isDirty={notesRequestDraft.trim() !== (notesRequestRow.notes ?? "").trim()}
                disabled={notesMutation.isPending || !hubWritesAllowed}
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  notesMutation.mutate({
                    id: notesRequestRow.id,
                    notes: notesRequestDraft.trim() || null,
                  });
                }}
              >
                {notesMutation.isPending ? "Saving…" : "Save notes"}
              </SaveChangesButton>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  notesMutation.reset();
                  setNotesRequestRow(null);
                }}
              >
                Cancel
              </Button>
            </div>
            {notesMutation.isError ? (
              <p className="mt-3 text-sm text-red-600">
                {notesMutation.error instanceof Error
                  ? notesMutation.error.message
                  : "Could not save notes."}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {aliasesRequestRow ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => {
            aliasesMutation.reset();
            setAliasesRequestRow(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="aml-cpn-aliases-request-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="aml-cpn-aliases-request-title" className="text-lg font-semibold text-slate-900">
                Edit MPN aliases
              </h2>
              <HoverHintWithGlyph
                hoverHint={`Alternate label spellings accepted during inbound receive for ${aliasesRequestRow.cpn} (row ${String(aliasesRequestRow.id)}). One alias per line or comma-separated.`}
              />
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="aml-cpn-aliases-request-input">MPN aliases</Label>
                <TextArea
                  id="aml-cpn-aliases-request-input"
                  rows={5}
                  value={aliasesRequestDraft}
                  disabled={!hubWritesAllowed}
                  onChange={(event) => setAliasesRequestDraft(event.target.value)}
                  placeholder="XL-060F/T1"
                />
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <SaveChangesButton
                size="sm"
                variant="primary"
                isDirty={
                  parseAmlMpnAliasesDraft(aliasesRequestDraft).join("\n") !==
                  parseAmlMpnAliasesDraft(amlMpnAliasesDraftFromRow(aliasesRequestRow)).join("\n")
                }
                disabled={aliasesMutation.isPending || !hubWritesAllowed}
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  aliasesMutation.mutate({
                    id: aliasesRequestRow.id,
                    mpn_aliases: parseAmlMpnAliasesDraft(aliasesRequestDraft),
                  });
                }}
              >
                {aliasesMutation.isPending ? "Saving…" : "Save aliases"}
              </SaveChangesButton>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  aliasesMutation.reset();
                  setAliasesRequestRow(null);
                }}
              >
                Cancel
              </Button>
            </div>
            {aliasesMutation.isError ? (
              <p className="mt-3 text-sm text-red-600">
                {aliasesMutation.error instanceof Error
                  ? aliasesMutation.error.message
                  : "Could not save MPN aliases."}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {amlQuery.isError ? (
        <p className="text-sm text-red-600">
          Could not load AML lines
          {amlQuery.error instanceof Error ? `: ${amlQuery.error.message}` : "."}
        </p>
      ) : amlQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : emptyMessage ? (
        <p className="text-sm text-slate-600">{emptyMessage}</p>
      ) : selectedRow ? (
        <>
          <div
            className="flex flex-wrap gap-1 border-b border-slate-200 pb-px"
            role="tablist"
            aria-label="Alternates for this CPN"
          >
            {sortedAlternates.map((row) => (
              <AmlAlternateTab
                key={row.id}
                row={row}
                selected={row.id === selectedAlternateId}
                onSelect={() => setSelectedAlternateId(row.id)}
              />
            ))}
          </div>

          <div
            role="tabpanel"
            id={`aml-alternate-panel-${selectedRow.id}`}
            aria-labelledby={`aml-alternate-tab-${selectedRow.id}`}
            className="rounded-b-lg rounded-tr-lg border border-t-0 border-slate-200 bg-white p-4 shadow-sm"
          >
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="block">
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-4">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={requestBusy || !hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    setNotesRequestDraft(selectedRow.notes ?? "");
                    setNotesRequestRow(selectedRow);
                  }}
                >
                  Edit notes
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={requestBusy || !hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    setAliasesRequestDraft(amlMpnAliasesDraftFromRow(selectedRow));
                    setAliasesRequestRow(selectedRow);
                  }}
                >
                  Edit MPN aliases
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={requestBusy || !hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    const label = selectedRow.active ? "deactivate" : "activate";
                    if (
                      window.confirm(
                        `Submit a request to ${label} this AML line for ${selectedRow.cpn}?`,
                      )
                    ) {
                      amlRequestMutation.mutate({
                        id: selectedRow.id,
                        body: selectedRow.active ? { kind: "deactivate" } : { kind: "activate" },
                      });
                    }
                  }}
                >
                  {selectedRow.active ? "Request deactivate" : "Request activate"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-700 hover:bg-red-50"
                  disabled={requestBusy || !hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    if (
                      window.confirm(
                        `Submit a request to delete this AML line (${selectedRow.cpn})? An approver must accept before it is removed.`,
                      )
                    ) {
                    amlRequestMutation.mutate({ id: selectedRow.id, body: { kind: "delete" } });
                  }
                }}
              >
                Request delete
              </Button>
              </div>
            </ReadOnlyHoverLayer>

            <AmlSourcingVendorsSection cpn={cpn} amlId={selectedRow.id} />

            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailItem label="Standard pack size">
                {selectedRow.standard_pack_size != null && selectedRow.standard_pack_size >= 1
                  ? selectedRow.standard_pack_size.toLocaleString()
                  : "—"}
              </DetailItem>
              <DetailItem label="Source (item)">{selectedRow.item_source?.trim() ? selectedRow.item_source : "—"}</DetailItem>
              <DetailItem label="Notes">
                <span className="whitespace-pre-wrap">{selectedRow.notes?.trim() ? selectedRow.notes : "—"}</span>
              </DetailItem>
              <DetailItem label="MPN aliases">
                {formatAmlMpnAliasesLine(selectedRow.mpn_aliases) ? (
                  <span className="font-mono text-xs whitespace-pre-wrap">
                    {formatAmlMpnAliasesLine(selectedRow.mpn_aliases)}
                  </span>
                ) : (
                  "—"
                )}
              </DetailItem>
              <DetailItem label="Status" hoverHint={AML_STATUS_COLUMN_HINT}>
                {selectedRow.status}
              </DetailItem>
              <DetailItem label="Active" hoverHint={AML_ACTIVE_COLUMN_HINT}>
                {selectedRow.active ? "Yes" : "No"}
              </DetailItem>
              <DetailItem label="End date" hoverHint={AML_END_DATE_COLUMN_HINT}>
                {formatAmlEndDate(selectedRow.end_date)}
              </DetailItem>
              <DetailItem label="Last updated">
                {Number.isNaN(new Date(selectedRow.updated_at).getTime())
                  ? selectedRow.updated_at
                  : new Date(selectedRow.updated_at).toLocaleString()}
                {selectedRow.updated_by ? (
                  <span className="mt-1 block text-xs text-slate-500">By {selectedRow.updated_by}</span>
                ) : null}
              </DetailItem>
              <DetailItem label="Request source (workflow)">
                {selectedRow.request_source?.trim() ? selectedRow.request_source : "—"}
              </DetailItem>
              <DetailItem label="Request context">
                {selectedRow.request_context?.trim() ? selectedRow.request_context : "—"}
              </DetailItem>
              <DetailItem label="Requested at">
                {selectedRow.requested_at
                  ? Number.isNaN(new Date(selectedRow.requested_at).getTime())
                    ? selectedRow.requested_at
                    : new Date(selectedRow.requested_at).toLocaleString()
                  : "—"}
              </DetailItem>
              <DetailItem label="Requested by (workflow id)">
                {selectedRow.requested_by != null && String(selectedRow.requested_by).trim() !== ""
                  ? String(selectedRow.requested_by)
                  : "—"}
              </DetailItem>
              <DetailItem label="Supporting documents">
                {formatSupportingDocsLine(selectedRow.supporting_docs) ?? "—"}
              </DetailItem>
            </dl>

            <Card className="mt-6 border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base">Change request history</CardTitle>
                <p className="mt-1 text-xs text-slate-500">
                  Queued changes from the integrator for this alternate (newest first). Pending rows still need an
                  approver.
                </p>
              </CardHeader>
              <CardBody className="pt-0">
                {historyQuery.isError ? (
                  <p className="text-sm text-red-600">
                    Could not load history
                    {historyQuery.error instanceof Error ? `: ${historyQuery.error.message}` : "."}
                  </p>
                ) : historyQuery.isLoading ? (
                  <p className="text-sm text-slate-500">Loading history…</p>
                ) : historyForAlternate.length === 0 ? (
                  <p className="text-sm text-slate-600">No change requests recorded for this alternate yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-slate-100">
                    <table className="min-w-full divide-y divide-slate-100 text-sm">
                      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        <tr>
                          <th className="px-3 py-2">Submitted</th>
                          <th className="px-3 py-2">Requester</th>
                          <th className="px-3 py-2">Action</th>
                          <th className="px-3 py-2">Detail</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Resolved</th>
                          <th className="px-3 py-2">Resolver</th>
                          <th className="px-3 py-2">Resolution notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {historyForAlternate.map((requestRow) => (
                          <tr key={requestRow.id}>
                            <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                              {new Date(requestRow.created_at).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {requestRow.requester_display_name !== requestRow.requested_by ? (
                                <HoverHintContent hoverHint={requestRow.requested_by} block>
                                  {requestRow.requester_display_name}
                                </HoverHintContent>
                              ) : (
                                requestRow.requester_display_name
                              )}
                            </td>
                            <td className="px-3 py-2 capitalize text-slate-700">{requestRow.action}</td>
                            <td className="max-w-xs whitespace-normal px-3 py-2 text-slate-700">
                              {summarizeAmlChangeRequestAction(requestRow.action, requestRow.payload)}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={
                                  requestRow.status === "pending"
                                    ? "rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800"
                                    : requestRow.status === "approved"
                                      ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800"
                                      : requestRow.status === "rejected"
                                        ? "rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800"
                                        : "rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900"
                                }
                              >
                                {requestRow.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                              {requestRow.resolved_at
                                ? new Date(requestRow.resolved_at).toLocaleString()
                                : "—"}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {requestRow.resolver_display_name != null &&
                              requestRow.resolved_by != null &&
                              requestRow.resolver_display_name !== requestRow.resolved_by ? (
                                <HoverHintContent hoverHint={requestRow.resolved_by} block>
                                  {requestRow.resolver_display_name ?? requestRow.resolved_by ?? "—"}
                                </HoverHintContent>
                              ) : (
                                requestRow.resolver_display_name ?? requestRow.resolved_by ?? "—"
                              )}
                            </td>
                            <td className="max-w-xs whitespace-normal px-3 py-2 text-slate-700">
                              {requestRow.resolution_notes?.trim() ? requestRow.resolution_notes : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      ) : null}

      {amlRequestMutation.isError && !notesRequestRow ? (
        <p className="text-sm text-red-600">
          {amlRequestMutation.error instanceof Error
            ? amlRequestMutation.error.message
            : "Could not submit request."}
        </p>
      ) : null}
    </div>
  );
}
